<!-- Toast Messages for embedded/web component mode only -->
@if (embeddedMode) {
  <p-toast
    position="top-right"
    [breakpoints]="{ '920px': { width: '100%', right: '0', left: '0' } }"
  />
}

<!-- Processing Cycle View - Compact Modern Design with Tabs -->
<div class="processing-cycle-view">
  <!-- Header with Back Button and Actions -->
  <div class="cycle-header">
    <div class="header-left">
      <p-button
        icon="pi pi-arrow-left"
        [text]="true"
        [rounded]="true"
        severity="contrast"
        (onClick)="goBack()"
        pTooltip="Back to Scheduled Order"
        styleClass="back-button"
      />
      <div class="header-title">
        <div *ngIf="processingCycle" class="title-wrapper">
          <span class="title-id"
            >#{{ processingCycle.ProcessingId || processingCycle.Id }}</span
          >
        </div>
        <p-skeleton *ngIf="isLoading" width="250px" height="2rem" />
      </div>
      <div class="header-actions">
        <p-button
          *ngIf="!isLoading && menuItems.length > 0"
          icon="pi pi-ellipsis-v"
          [text]="true"
          [rounded]="true"
          severity="contrast"
          (onClick)="menu.toggle($event)"
          pTooltip="Actions"
        />
        <p-menu #menu [model]="menuItems" [popup]="true" appendTo="body" />
      </div>
    </div>
  </div>

  <!-- Loading State -->
  <div *ngIf="isLoading" class="loading-container">
    <div class="two-column-layout">
      <!-- Left Column Skeleton -->
      <div class="left-column-skeleton">
        <div class="skeleton-card">
          <!-- Status Section Skeleton -->
          <div class="skeleton-section">
            <div class="skeleton-header">
              <p-skeleton width="60%" height="1.5rem" />
              <p-skeleton
                width="80px"
                height="2rem"
                styleClass="skeleton-tag"
              />
            </div>
            <div class="skeleton-times">
              <p-skeleton width="45%" height="3rem" />
              <p-skeleton width="45%" height="3rem" />
            </div>
          </div>

          <div class="skeleton-divider"></div>

          <!-- General Info Section Skeleton -->
          <div class="skeleton-section">
            <p-skeleton width="40%" height="1rem" styleClass="mb-3" />
            <div class="skeleton-info-grid">
              <p-skeleton width="100%" height="3rem" />
              <p-skeleton width="100%" height="3rem" />
              <p-skeleton width="100%" height="3rem" />
              <p-skeleton width="100%" height="3rem" />
            </div>
          </div>
        </div>
      </div>

      <!-- Right Column Skeleton -->
      <div class="right-column-skeleton">
        <div class="skeleton-tabs">
          <div class="skeleton-tab-header">
            <p-skeleton width="150px" height="2.5rem" styleClass="mr-2" />
            <p-skeleton width="150px" height="2.5rem" />
          </div>
          <div class="skeleton-tab-content">
            <p-skeleton width="100%" height="4rem" styleClass="mb-3" />
            <p-skeleton width="100%" height="4rem" styleClass="mb-3" />
            <p-skeleton width="100%" height="6rem" />
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Content -->
  <div *ngIf="!isLoading && processingCycle" class="cycle-content">
    <!-- Two Column Layout -->
    <div class="two-column-layout">
      <!-- Left Column: Status & General Information -->
      <div class="left-column">
        <div class="left-column-card" [class.collapsed]="isStatusCollapsed">
          <!-- Status Section -->
          <div
            class="info-section status-section"
            [class.collapsed]="isStatusCollapsed"
          >
            <div class="status-header" [class.collapsed]="isStatusCollapsed">
              <div class="status-main">
                <button
                  pButton
                  [icon]="
                    isStatusCollapsed
                      ? 'pi pi-chevron-down'
                      : 'pi pi-chevron-up'
                  "
                  [text]="true"
                  [rounded]="true"
                  severity="contrast"
                  (click)="toggleStatusCollapse()"
                  class="collapse-button mobile-only"
                  pTooltip="Toggle status section"
                  aria-label="Toggle status details"
                ></button>
                <span class="status-label">Status</span>
                <p-tag
                  [value]="processingCycle.Status"
                  [severity]="getStatusSeverity(processingCycle.Status)"
                  styleClass="status-tag"
                />
                <a
                  *ngIf="
                    processingCycle.ProcessingErrorCode &&
                    processingCycle.Status === 'Failed'
                  "
                  [href]="getErrorDocUrl(processingCycle.ProcessingErrorCode)"
                  target="_blank"
                  class="error-code-link"
                >
                  ({{ processingCycle.ProcessingErrorCode }})
                </a>
              </div>
              <button
                pButton
                label="Logs"
                icon="pi pi-list"
                class="p-button-sm p-button-outlined view-logs-btn"
                (click)="viewLogs()"
                aria-label="View processing logs"
              ></button>
            </div>

            <!-- Collapsible Content -->
            <div
              class="status-collapsible-content"
              [class.mobile-hidden]="isStatusCollapsed"
            >
              <!-- Processing Times (if not processing) -->
              <div
                *ngIf="processingCycle.Status !== 'Processing'"
                class="processing-times"
              >
                <div class="time-item">
                  <span class="time-label">Start</span>
                  <span class="time-value">{{
                    formatDate(processingCycle.StartDateUtc)
                  }}</span>
                </div>
                <div class="time-item">
                  <span class="time-label">End</span>
                  <span class="time-value">{{
                    formatDate(processingCycle.EndDateUtc)
                  }}</span>
                </div>
              </div>

              <!-- Failure Reason (if failed) -->
              <div
                *ngIf="
                  processingCycle.ScheduledOrderFailureReason &&
                  processingCycle.Status !== 'Processing' &&
                  processingCycle.Status !== 'Success'
                "
                class="failure-reason"
              >
                <i class="pi pi-exclamation-triangle"></i>
                <span>{{ processingCycle.ScheduledOrderFailureReason }}</span>
              </div>

              <!-- Error 2001 Warning -->
              <div
                *ngIf="
                  processingCycle.ProcessingErrorCode === 2001 &&
                  processingCycle.Status !== 'Processing'
                "
                class="error-warning"
              >
                <i class="pi pi-exclamation-circle"></i>
                <div class="warning-content">
                  <p>
                    This Scheduled Order failed because the Payment Gateway did
                    not respond to the payment request. Please login to the
                    Payment Gateway to confirm if a transaction for this payment
                    was created.
                  </p>
                  <ul>
                    <li>
                      If a transaction for this payment
                      <strong>was created</strong>, please copy the amount and
                      id for the transaction to
                      <a
                        href="javascript:void(0)"
                        (click)="openCompleteDialog()"
                        class="complete-cycle-link"
                      >
                        complete this processing cycle </a
                      >.
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <!-- Divider -->
          <div
            class="section-divider"
            [class.mobile-hidden]="isStatusCollapsed"
          ></div>

          <!-- General Information -->
          <div
            class="info-section general-info-section"
            [class.mobile-hidden]="isStatusCollapsed"
          >
            <h3 class="section-title">General Information</h3>
            <div class="info-grid">
              <div class="info-item">
                <span class="info-label">Scheduled Order ID</span>
                <span class="info-value"
                  ><code>#{{ processingCycle.ScheduledOrderId }}</code></span
                >
              </div>
              <div class="info-item">
                <span class="info-label">Scheduled Order Total</span>
                <span class="info-value">{{
                  formatCurrency(processingCycle.Total)
                }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Order ID</span>
                <span class="info-value"
                  ><code>{{ processingCycle.ClientOrderId || "-" }}</code></span
                >
              </div>
              <div class="info-item">
                <span class="info-label">Charged Total</span>
                <span class="info-value font-semibold">{{
                  formatCurrency(processingCycle.ChargedTotal)
                }}</span>
              </div>
              <div *ngIf="processingCycle.Notes" class="info-item full-width">
                <span class="info-label">Notes</span>
                <span class="info-value">{{ processingCycle.Notes }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Column: Payment & Order Information Tabs -->
      <div class="right-column">
        <div class="tabs-container">
          <p-tabs value="0" styleClass="processing-tabs" scrollable>
            <p-tablist>
              <p-tab *ngIf="scheduledOrder?.PaymentMethodName" value="0"
                >Payment Information</p-tab
              >
              <p-tab [value]="scheduledOrder?.PaymentMethodName ? '1' : '0'"
                >Order Information</p-tab
              >
            </p-tablist>
            <p-tabpanels>
              <!-- Payment Information Tab -->
              <p-tabpanel *ngIf="scheduledOrder?.PaymentMethodName" value="0">
                <div class="tab-content">
                  <div class="info-grid">
                    <div class="info-item">
                      <span class="info-label">Gateway Transaction ID</span>
                      <span class="info-value"
                        ><code>{{
                          processingCycle.PaymentGatewayTransactionId || "-"
                        }}</code></span
                      >
                    </div>
                    <div
                      *ngIf="processingCycle.PaymentGatewayErrorCode"
                      class="info-item"
                    >
                      <span class="info-label">Gateway Error Code</span>
                      <span class="info-value text-red-600">{{
                        processingCycle.PaymentGatewayErrorCode
                      }}</span>
                    </div>
                    <div
                      *ngIf="processingCycle.PaymentGatewayErrorDescription"
                      class="info-item full-width"
                    >
                      <span class="info-label">Gateway Error Description</span>
                      <span class="info-value text-red-600">{{
                        processingCycle.PaymentGatewayErrorDescription
                      }}</span>
                    </div>
                    <div
                      *ngIf="processingCycle.PaymentGatewayResponse"
                      class="info-item full-width"
                    >
                      <p-accordion>
                        <p-accordionpanel value="0">
                          <p-accordionheader
                            >Gateway Raw Response</p-accordionheader
                          >
                          <p-accordioncontent>
                            <div class="json-viewer">
                              <div class="json-actions">
                                <p-button
                                  icon="pi pi-copy"
                                  [text]="true"
                                  size="small"
                                  pTooltip="Copy to clipboard"
                                  (onClick)="
                                    copyToClipboard(
                                      processingCycle.PaymentGatewayResponse
                                    )
                                  "
                                />
                              </div>
                              <ngx-json-viewer
                                [json]="parsedPaymentGatewayResponse"
                              />
                            </div>
                          </p-accordioncontent>
                        </p-accordionpanel>
                      </p-accordion>
                    </div>
                  </div>
                </div>
              </p-tabpanel>

              <!-- Order Information Tab -->
              <p-tabpanel
                [value]="scheduledOrder?.PaymentMethodName ? '1' : '0'"
              >
                <div class="tab-content">
                  <div class="info-grid">
                    <div class="info-item">
                      <span class="info-label">Order ID</span>
                      <span class="info-value"
                        ><code>{{
                          processingCycle.ClientOrderId || "-"
                        }}</code></span
                      >
                    </div>

                    <!-- Order Creation Request -->
                    <div
                      *ngIf="processingCycle.ClientOrderCreationRequestJson"
                      class="info-item full-width"
                    >
                      <p-accordion>
                        <p-accordionpanel value="0">
                          <p-accordionheader
                            >Order Creation Request</p-accordionheader
                          >
                          <p-accordioncontent>
                            <div class="json-viewer">
                              <div class="json-actions">
                                <p-button
                                  icon="pi pi-copy"
                                  [text]="true"
                                  size="small"
                                  pTooltip="Copy to clipboard"
                                  (onClick)="
                                    copyToClipboard(
                                      processingCycle.ClientOrderCreationRequestJson
                                    )
                                  "
                                />
                                <p-button
                                  icon="pi pi-download"
                                  [text]="true"
                                  size="small"
                                  pTooltip="Download JSON"
                                  (onClick)="
                                    downloadJson(
                                      processingCycle.ClientOrderCreationRequestJson,
                                      processingCycle.ScheduledOrderId +
                                        '-' +
                                        processingCycle.ProcessingId +
                                        '-create-request.json'
                                    )
                                  "
                                />
                              </div>
                              <ngx-json-viewer
                                [json]="parsedClientOrderCreationRequest"
                              />
                            </div>
                          </p-accordioncontent>
                        </p-accordionpanel>
                      </p-accordion>
                    </div>

                    <!-- Order Creation Response -->
                    <div
                      *ngIf="processingCycle.ClientOrderCreationResponseJson"
                      class="info-item full-width"
                    >
                      <p-accordion>
                        <p-accordionpanel value="0">
                          <p-accordionheader
                            >Order Creation Response</p-accordionheader
                          >
                          <p-accordioncontent>
                            <div class="json-viewer">
                              <div class="json-actions">
                                <p-button
                                  icon="pi pi-copy"
                                  [text]="true"
                                  size="small"
                                  pTooltip="Copy to clipboard"
                                  (onClick)="
                                    copyToClipboard(
                                      processingCycle.ClientOrderCreationResponseJson
                                    )
                                  "
                                />
                                <p-button
                                  icon="pi pi-download"
                                  [text]="true"
                                  size="small"
                                  pTooltip="Download JSON"
                                  (onClick)="
                                    downloadJson(
                                      processingCycle.ClientOrderCreationResponseJson,
                                      processingCycle.ScheduledOrderId +
                                        '-' +
                                        processingCycle.ProcessingId +
                                        '-create-response.json'
                                    )
                                  "
                                />
                              </div>
                              <ngx-json-viewer
                                [json]="parsedClientOrderCreationResponse"
                              />
                            </div>
                          </p-accordioncontent>
                        </p-accordionpanel>
                      </p-accordion>
                    </div>

                    <!-- Order Update Request -->
                    <div
                      *ngIf="processingCycle.ClientOrderUpdateRequestJson"
                      class="info-item full-width"
                    >
                      <p-accordion>
                        <p-accordionpanel value="0">
                          <p-accordionheader
                            >Order Update Request</p-accordionheader
                          >
                          <p-accordioncontent>
                            <div class="json-viewer">
                              <div class="json-actions">
                                <p-button
                                  icon="pi pi-copy"
                                  [text]="true"
                                  size="small"
                                  pTooltip="Copy to clipboard"
                                  (onClick)="
                                    copyToClipboard(
                                      processingCycle.ClientOrderUpdateRequestJson
                                    )
                                  "
                                />
                                <p-button
                                  icon="pi pi-download"
                                  [text]="true"
                                  size="small"
                                  pTooltip="Download JSON"
                                  (onClick)="
                                    downloadJson(
                                      processingCycle.ClientOrderUpdateRequestJson,
                                      processingCycle.ScheduledOrderId +
                                        '-' +
                                        processingCycle.ProcessingId +
                                        '-update-request.json'
                                    )
                                  "
                                />
                              </div>
                              <ngx-json-viewer
                                [json]="parsedClientOrderUpdateRequest"
                              />
                            </div>
                          </p-accordioncontent>
                        </p-accordionpanel>
                      </p-accordion>
                    </div>

                    <!-- Order Update Response -->
                    <div
                      *ngIf="processingCycle.ClientOrderUpdateResponseJson"
                      class="info-item full-width"
                    >
                      <p-accordion>
                        <p-accordionpanel value="0">
                          <p-accordionheader
                            >Order Update Response</p-accordionheader
                          >
                          <p-accordioncontent>
                            <div class="json-viewer">
                              <div class="json-actions">
                                <p-button
                                  icon="pi pi-copy"
                                  [text]="true"
                                  size="small"
                                  pTooltip="Copy to clipboard"
                                  (onClick)="
                                    copyToClipboard(
                                      processingCycle.ClientOrderUpdateResponseJson
                                    )
                                  "
                                />
                                <p-button
                                  icon="pi pi-download"
                                  [text]="true"
                                  size="small"
                                  pTooltip="Download JSON"
                                  (onClick)="
                                    downloadJson(
                                      processingCycle.ClientOrderUpdateResponseJson,
                                      processingCycle.ScheduledOrderId +
                                        '-' +
                                        processingCycle.ProcessingId +
                                        '-update-response.json'
                                    )
                                  "
                                />
                              </div>
                              <ngx-json-viewer
                                [json]="parsedClientOrderUpdateResponse"
                              />
                            </div>
                          </p-accordioncontent>
                        </p-accordionpanel>
                      </p-accordion>
                    </div>
                  </div>
                </div>
              </p-tabpanel>
            </p-tabpanels>
          </p-tabs>
        </div>
      </div>
    </div>
  </div>
</div>
